const YOUTUBE_ID_RE = /^[a-zA-Z0-9_-]{11}$/;

/**
 * Extract a YouTube video ID from a URL.
 *
 * Supported formats:
 *  - https://www.youtube.com/watch?v=VIDEO_ID
 *  - https://youtube.com/watch?v=VIDEO_ID
 *  - https://youtu.be/VIDEO_ID
 *  - https://www.youtube.com/embed/VIDEO_ID
 *  - https://www.youtube.com/shorts/VIDEO_ID
 *  - Any of the above with extra query params
 *
 * Returns the 11-character video ID, or null if the URL is not a recognised
 * YouTube format.
 */
export function extractYouTubeId(url: string): string | null {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }

  const hostname = parsed.hostname.replace(/^www\./, '');

  // youtu.be/VIDEO_ID
  if (hostname === 'youtu.be') {
    const id = parsed.pathname.slice(1).split('/')[0];
    return YOUTUBE_ID_RE.test(id) ? id : null;
  }

  // youtube.com variants
  if (hostname !== 'youtube.com') return null;

  // /watch?v=VIDEO_ID
  if (parsed.pathname === '/watch') {
    const id = parsed.searchParams.get('v');
    return id && YOUTUBE_ID_RE.test(id) ? id : null;
  }

  // /embed/VIDEO_ID or /shorts/VIDEO_ID
  const match = parsed.pathname.match(/^\/(embed|shorts)\/([a-zA-Z0-9_-]{11})/);
  if (match) return match[2];

  return null;
}

/** Build a YouTube embed URL from a video ID. */
export function youTubeEmbedUrl(videoId: string): string {
  return `https://www.youtube.com/embed/${videoId}`;
}
